import React from 'react';
import { StepProps, SPECIALTY_COLORS } from '../../types';
import { motion } from 'framer-motion';
import { Target, BarChart3, Sparkles, ArrowRight, CheckCircle2 } from 'lucide-react';
const CHAPTERS = [{
  id: 'segmentation',
  title: 'Segmentation & Targeting',
  description: 'Rank what drives and blocks your specialty — then see how your instincts compare to the data.',
  icon: Target,
  targetStep: 8,
  gradient: 'from-blue-500 to-indigo-600',
  bgLight: 'bg-blue-50',
  textColor: 'text-blue-600',
  borderColor: 'border-blue-200'
}, {
  id: 'arguments',
  title: 'Arguments & Data',
  description: 'Pick the features and framings that will resonate most — then see what the evidence says.',
  icon: BarChart3,
  targetStep: 9,
  gradient: 'from-amber-500 to-orange-600',
  bgLight: 'bg-amber-50',
  textColor: 'text-amber-600',
  borderColor: 'border-amber-200'
}, {
  id: 'motivations',
  title: 'Motivations',
  description: 'Find the right emotional and rational frame to move your specialty to action.',
  icon: Sparkles,
  targetStep: 10,
  gradient: 'from-emerald-500 to-teal-600',
  bgLight: 'bg-emerald-50',
  textColor: 'text-emerald-600',
  borderColor: 'border-emerald-200'
}];
export function StrategyMenu({
  state,
  goToStep,
  nextStep
}: StepProps) {
  const role = state.selectedSpecialty || 'Nephrologist';
  const color = SPECIALTY_COLORS[role];
  const completed = state.completedChapters || [];
  const allDone = CHAPTERS.every(ch => completed.includes(ch.id));
  return <div className="flex-grow flex flex-col py-12 px-6 overflow-y-auto pt-20" data-id="element-729">
      <div className="max-w-3xl w-full mx-auto" data-id="element-730">
        <div className="text-center mb-12" data-id="element-731">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-5" style={{
          backgroundColor: `${color}15`,
          color
        }} data-id="element-732">
            <Target size={28} data-id="element-733" />
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-dark mb-3" data-id="element-734">
            Strategy Exercises
          </h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto" data-id="element-735">
            Choose a chapter to explore. Complete all three to build your
            group's strategy card for{' '}
            <span className="font-semibold" style={{
            color
          }} data-id="element-736">
              {role}s
            </span>
            .
          </p>
          {completed.length > 0 && !allDone && <p className="text-sm text-gray-400 mt-2" data-id="element-737">
              {completed.length} of 3 completed
            </p>}
        </div>

        <div className="space-y-4 mb-12" data-id="element-738">
          {CHAPTERS.map((chapter, i) => {
          const Icon = chapter.icon;
          const isDone = completed.includes(chapter.id);
          return <motion.button key={chapter.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.1,
            duration: 0.4
          }} whileHover={{
            scale: 1.01
          }} whileTap={{
            scale: 0.99
          }} onClick={() => goToStep(chapter.targetStep)} className={`w-full p-6 rounded-2xl border-2 text-left flex items-center transition-all duration-200 group ${isDone ? `${chapter.bgLight} ${chapter.borderColor} opacity-80` : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-md'}`} data-id="element-739">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mr-5 flex-shrink-0 ${isDone ? `${chapter.bgLight} ${chapter.textColor}` : 'bg-gradient-to-br text-white'} ${!isDone ? chapter.gradient : ''}`} data-id="element-740">
                  {isDone ? <CheckCircle2 size={26} data-id="element-741" /> : <Icon size={26} data-id="element-742" />}
                </div>
                <div className="flex-grow min-w-0" data-id="element-743">
                  <div className="flex items-center" data-id="element-744">
                    <h3 className={`text-xl font-bold ${isDone ? 'text-gray-500' : 'text-dark'}`} data-id="element-745">
                      {chapter.title}
                    </h3>
                    {isDone && <span className={`ml-3 text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${chapter.bgLight} ${chapter.textColor}`} data-id="element-746">
                        Done
                      </span>}
                  </div>
                  <p className={`mt-1 text-sm leading-relaxed ${isDone ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-747">
                    {chapter.description}
                  </p>
                </div>
                <ArrowRight className={`ml-4 flex-shrink-0 transition-transform group-hover:translate-x-1 ${isDone ? 'text-gray-300' : 'text-gray-400'}`} size={20} data-id="element-748" />
              </motion.button>;
        })}
        </div>

        {allDone && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center" data-id="element-749">
            <div className="bg-green-50 border border-green-200 rounded-2xl p-6 mb-6" data-id="element-750">
              <CheckCircle2 className="text-green-500 mx-auto mb-2" size={28} data-id="element-751" />
              <p className="text-green-800 font-bold text-lg" data-id="element-752">
                All strategy exercises complete!
              </p>
              <p className="text-green-600 text-sm mt-1" data-id="element-753">
                Time to build your group's strategy card.
              </p>
            </div>
            <button onClick={nextStep} className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-dark rounded-full hover:bg-black transition-colors" data-id="element-754">
              Build Your Strategy Card
              <ArrowRight className="ml-2 w-5 h-5" data-id="element-755" />
            </button>
          </motion.div>}
      </div>
    </div>;
}